import { TaxReturnTOC, TaxReturnTOCItem, TaxReturnTOCKey } from '../model';
import {
    areTocKeysEqual,
    findTocItemById,
    getTocItemFormPageName,
    getTocItemFromKeyMap,
    isIsolatedTocItem,
    resolveTocItemPageIndex,
} from './TaxReturnTOCUtils';

export type NavigationKind = 'grouped-page' | 'isolated-item' | 'folder';

export interface NavigationTarget {
    itemsToLoad: TaxReturnTOCItem[];
    selectedKey: TaxReturnTOCKey;
    selectedItemId: string;
    pageIndex: number;
    kind: NavigationKind;
}

export interface FormNavigationState {
    id?: string;
    parentId?: string | null;
    tocKey?: TaxReturnTOCKey | null;
    pageIndex?: number | null;
}

export const isVisibleInFormToc = (item: TaxReturnTOCItem | undefined | null): boolean => {
    return Boolean(item) && item!.taxReturnTOCKey !== undefined && item!.taxReturnTOCKey !== null && item!.isVisibleInTOCView === true;
};

export const getVisibleChildren = (parent: TaxReturnTOCItem | undefined | null): TaxReturnTOCItem[] => {
    if (!parent?.children) {
        return [];
    }

    return parent.children.filter((child) => isVisibleInFormToc(child));
};

const getFirstVisibleSelectableDescendant = (item: TaxReturnTOCItem): TaxReturnTOCItem | undefined => {
    const children: TaxReturnTOCItem[] | null | undefined = item.children;

    if (!children || children.length === 0) {
        return item.taxReturnTOCKey ? item : undefined;
    }

    for (const child of children) {
        if (!(child.isVisibleInTOCView ?? false)) {
            continue;
        }

        const descendant: TaxReturnTOCItem | undefined = getFirstVisibleSelectableDescendant(child);
        if (descendant) {
            return descendant;
        }
    }

    return undefined;
};

export const resolveSelectableItem = (item: TaxReturnTOCItem): TaxReturnTOCItem | undefined => {
    if (item.taxReturnTOCKey) {
        return item;
    }

    return getFirstVisibleSelectableDescendant(item);
};

const buildTargetFromResolvedItem = (
    resolvedItem: TaxReturnTOCItem,
    tocItems: TaxReturnTOCItem[] | null | undefined
): NavigationTarget | undefined => {
    if (!resolvedItem.taxReturnTOCKey || !resolvedItem.id) {
        return undefined;
    }

    if (resolvedItem.children && resolvedItem.children.length > 0) {
        const visibleChildren = getVisibleChildren(resolvedItem);
        if (visibleChildren.length === 0) {
            return undefined;
        }

        const selectedChild = visibleChildren[0];
        if (!selectedChild?.taxReturnTOCKey || !selectedChild.id) {
            return undefined;
        }

        return {
            itemsToLoad: visibleChildren,
            selectedKey: selectedChild.taxReturnTOCKey,
            selectedItemId: selectedChild.id,
            pageIndex: resolveTocItemPageIndex(selectedChild, tocItems),
            kind: 'folder',
        };
    }

    const parentElement: TaxReturnTOCItem | undefined = findTocItemById(tocItems, resolvedItem.parentId ?? undefined);
    if (parentElement?.taxReturnTOCKey) {
        const visibleChildren = getVisibleChildren(parentElement);
        const selectedChild =
            visibleChildren.find((child) => areTocKeysEqual(child.taxReturnTOCKey, resolvedItem.taxReturnTOCKey)) ??
            visibleChildren.find(
                (child) => child.id === resolvedItem.id && getTocItemFormPageName(child) === getTocItemFormPageName(resolvedItem)
            ) ??
            visibleChildren.find((child) => child.id === resolvedItem.id);
        if (!selectedChild?.taxReturnTOCKey || !selectedChild.id) {
            return undefined;
        }

        return {
            itemsToLoad: visibleChildren,
            selectedKey: selectedChild.taxReturnTOCKey,
            selectedItemId: selectedChild.id,
            pageIndex: resolveTocItemPageIndex(selectedChild, tocItems),
            kind: 'grouped-page',
        };
    }

    return {
        itemsToLoad: [resolvedItem],
        selectedKey: resolvedItem.taxReturnTOCKey,
        selectedItemId: resolvedItem.id,
        pageIndex: resolveTocItemPageIndex(resolvedItem, tocItems),
        kind: isIsolatedTocItem(resolvedItem, tocItems) ? 'isolated-item' : 'grouped-page',
    };
};

export const resolveNavigationTarget = (item: TaxReturnTOCItem, tocData: TaxReturnTOC | null | undefined): NavigationTarget | undefined => {
    const resolvedItem = resolveSelectableItem(item);
    if (!resolvedItem) {
        return undefined;
    }

    return buildTargetFromResolvedItem(resolvedItem, tocData?.items);
};

export const resolveNavigationTargetBySectionIndex = (
    navigationItems: TaxReturnTOCItem[],
    index: number,
    tocData: TaxReturnTOC | null | undefined,
    options?: { goToLastPage?: boolean }
): NavigationTarget | undefined => {
    if (index < 0 || index >= navigationItems.length) {
        return undefined;
    }

    const targetItem = navigationItems[index];
    if (!targetItem) {
        return undefined;
    }

    if (targetItem.children && targetItem.children.length > 0) {
        const visibleChildren = getVisibleChildren(targetItem);
        if (visibleChildren.length === 0) {
            return undefined;
        }

        const selectedChild = options?.goToLastPage === true ? visibleChildren[visibleChildren.length - 1] : visibleChildren[0];
        if (!selectedChild?.taxReturnTOCKey || !selectedChild.id) {
            return undefined;
        }

        return {
            itemsToLoad: visibleChildren,
            selectedKey: selectedChild.taxReturnTOCKey,
            selectedItemId: selectedChild.id,
            pageIndex: resolveTocItemPageIndex(selectedChild, tocData?.items),
            kind: 'grouped-page',
        };
    }

    if (!targetItem.id || !targetItem.taxReturnTOCKey) {
        return undefined;
    }

    return {
        itemsToLoad: [targetItem],
        selectedKey: targetItem.taxReturnTOCKey,
        selectedItemId: targetItem.id,
        pageIndex: resolveTocItemPageIndex(targetItem, tocData?.items),
        kind: isIsolatedTocItem(targetItem, tocData?.items) ? 'isolated-item' : 'grouped-page',
    };
};

const getRootSection = (
    tocItems: TaxReturnTOCItem[] | null | undefined,
    parentId: string | null | undefined
): TaxReturnTOCItem | undefined => {
    let currentSection: TaxReturnTOCItem | undefined = findTocItemById(tocItems, parentId ?? undefined);

    while (currentSection?.parentId) {
        currentSection = findTocItemById(tocItems, currentSection.parentId ?? undefined);
    }

    return currentSection;
};

export const buildSectionNavigationList = (
    tocData: TaxReturnTOC | null | undefined,
    currentState: FormNavigationState | undefined
): TaxReturnTOCItem[] => {
    if (!currentState?.parentId) {
        return (tocData?.items?.filter((item) => (item.children?.length ?? 0) === 0 && isVisibleInFormToc(item)) ?? []).sort(
            (a, b) => (a.index ?? 0) - (b.index ?? 0)
        );
    }

    const currentSection = getRootSection(tocData?.items, currentState.parentId);
    const items = currentSection?.children?.filter((item) => isVisibleInFormToc(item)) ?? [];

    return [...items].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
};

export const resolveNavigationIndex = (
    navigationItems: TaxReturnTOCItem[],
    currentState: FormNavigationState | undefined,
    tocData: TaxReturnTOC | null | undefined,
    tocReferenceMap: Map<TaxReturnTOCKey, TaxReturnTOCItem>
): number => {
    const currentId = currentState?.id ?? '';
    const currentParentId = currentState?.parentId ?? '';

    let currentIndex = navigationItems.findIndex((item) => item.id === currentId || item.id === currentParentId);

    if (currentIndex < 0) {
        const currentItemFromKey: TaxReturnTOCItem | undefined = currentState?.tocKey
            ? getTocItemFromKeyMap(tocReferenceMap, currentState.tocKey)
            : undefined;
        const currentItemFromId: TaxReturnTOCItem | undefined = currentItemFromKey ? undefined : findTocItemById(tocData?.items, currentId);
        const currentItemFromLookup: TaxReturnTOCItem | undefined = currentItemFromKey ?? currentItemFromId;
        const currentItemIndex: number | null | undefined = currentItemFromLookup?.index ?? currentState?.pageIndex ?? null;

        if (currentItemIndex !== null && currentItemIndex !== undefined) {
            currentIndex = navigationItems.findIndex((item) => item.index === currentItemIndex);
        }
    }

    return currentIndex >= 0 ? currentIndex : 0;
};

export const isSameLoadedIsolatedItemReNavigation = (
    target: NavigationTarget,
    currentState: FormNavigationState | undefined,
    selectedItem: TaxReturnTOCItem | undefined,
    tocItems: TaxReturnTOCItem[] | null | undefined,
    isLoaded: boolean
): boolean => {
    if (target.kind !== 'isolated-item' || !isLoaded) {
        return false;
    }

    if (!selectedItem || !currentState?.id || target.selectedItemId !== currentState.id) {
        return false;
    }

    // Duplicate TOC ids can exist; when state has a tocKey, require it to match.
    // If tocKey is absent (legacy/incomplete state), fall back to id-only matching.
    if (currentState.tocKey && !areTocKeysEqual(target.selectedKey, currentState.tocKey)) {
        return false;
    }

    return isIsolatedTocItem(selectedItem, tocItems);
};

export const isIntraPdfNavigation = (
    selectedItem: TaxReturnTOCItem | undefined,
    currentState: FormNavigationState | undefined,
    tocItems: TaxReturnTOCItem[] | null | undefined,
    loadedTocItemId: string | null,
    pdfSyncPhase?: 'idle' | 'loading-document' | 'syncing-page' | 'ready'
): boolean => {
    if (!selectedItem || !currentState) {
        return false;
    }

    const isViewerReadyForIntraNav: boolean = loadedTocItemId !== null || pdfSyncPhase === 'ready' || pdfSyncPhase === 'syncing-page';

    if (!isViewerReadyForIntraNav) {
        return false;
    }

    return selectedItem.parentId != null && selectedItem.parentId === currentState.parentId && !isIsolatedTocItem(selectedItem, tocItems);
};

export const getFirstVisibleChildId = (item: TaxReturnTOCItem): string | undefined => {
    const visibleChildren = getVisibleChildren(item);
    if (visibleChildren.length > 0) {
        return visibleChildren[0].id;
    }

    return item.children?.[0]?.id;
};

export const getApplicableGroupedFormChildren = (parent: TaxReturnTOCItem | undefined | null, entityId?: number): TaxReturnTOCItem[] => {
    const visibleChildren = getVisibleChildren(parent);
    if (entityId !== undefined && entityId > 0) {
        return visibleChildren.filter((child) => !child.isEnumerated);
    }

    return visibleChildren;
};

export const isGroupedFormPageInvalidForEntity = (
    data: TaxReturnTOC,
    itemIdToExpand: string | undefined,
    currentState: FormNavigationState | undefined,
    entityId?: number
): boolean => {
    if (!currentState?.parentId) {
        return false;
    }

    if (!itemIdToExpand || !findTocItemById(data.items, itemIdToExpand)) {
        return true;
    }

    const parentElement = findTocItemById(data.items, currentState.parentId);
    if (!parentElement?.taxReturnTOCKey) {
        return false;
    }

    const applicableChildren = getApplicableGroupedFormChildren(parentElement, entityId);
    if (applicableChildren.length === 0) {
        return false;
    }

    const expandedItem = findTocItemById(data.items, itemIdToExpand);
    if (expandedItem?.parentId !== currentState.parentId) {
        return true;
    }

    const currentChildIndex = applicableChildren.findIndex((child) => child.id === itemIdToExpand);
    const pageIndex = currentState.pageIndex ?? -1;

    return currentChildIndex < 0 || pageIndex < 0 || pageIndex >= applicableChildren.length;
};
